import React, { memo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { InlineForm } from 'components';
import { useConfirmation } from 'context';
import { setDataOnArray } from 'helpers';

function ResourceManagerComponent ({
  data,
  children,
  renderItem,
  onCreate,
  onUpdate,
  onRemove,
  onChangeItem,
  disabled,
  ...other
}) {
  const [currentItem, setCurrentItem] = useState({});
  const [currentValues, setCurrentValues] = useState([]);
  const confirm = useConfirmation();

  useEffect(() => setCurrentValues(data), [data]);

  const handleSubmit = useCallback(async payload => {
    let response;
    if (currentItem.id) {
      response = await onUpdate(payload, currentItem.id);
      const index = data.findIndex(item => item.id === response.id);
      if (index >= 0) {
        data[index] = response;
      }
    } else {
      response = await onCreate(payload);
      data.push(response);
    }
    setCurrentValues(prev => setDataOnArray(prev, response));
    if (onChangeItem) {
      onChangeItem(data);
    }
  }, [currentItem.id, data, onChangeItem, onCreate, onUpdate]);

  const handleCancel = useCallback(() => setCurrentItem({}), []);

  const handleSelect = useCallback(item => () => setCurrentItem(item), []);

  const handleRemove = useCallback(itemId => () => {
    confirm({
      title: 'Tem certeza de que deseja excluir?',
      size: 'xs',
      onConfirm: async () => {
        await onRemove(itemId);
        setCurrentItem(prev => prev.id === itemId ? {} : prev);
        setCurrentValues(prev => prev?.filter(item => item.id !== itemId));
        const index = data.findIndex(item => item.id === itemId);
        if (index >= 0) {
          data.splice(index, 1);
        }
        if (onChangeItem) {
          onChangeItem(data);
        }
      }
    });
    return true;
  }, [confirm, data, onChangeItem, onRemove]);

  const _handleSubmit = !!currentItem.id || !!onCreate ? handleSubmit : null;

  return (
    <>
      {currentValues?.map((item, index) => (
        renderItem({
          item,
          index,
          onRemove: handleRemove(item.id),
          onSelect: handleSelect(item),
          selected: currentItem === item
        })
      ))}
      <InlineForm
        {...other}
        data={currentItem}
        onSubmit={_handleSubmit}
        onCancel={handleCancel}
        disabled={disabled || !_handleSubmit}
        confirmLabel={currentItem.id ? 'Alterar' : 'Adicionar'}
        cancelLabel="Limpar"
        clearAfterSave
      >
        {children}
      </InlineForm>
    </>
  );
}

ResourceManagerComponent.propTypes = {
  data: PropTypes.array.isRequired,
  children: PropTypes.node.isRequired,
  renderItem: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  onRemove: PropTypes.func,
  onChangeItem: PropTypes.func,
  disabled: PropTypes.bool
};

ResourceManagerComponent.defaultProps = {
  data: []
};

export default memo(ResourceManagerComponent);
